import type { DayCardPreview, MapConfig, Settings } from '../../api/types'
import { VEHICLE_ART, VEHICLE_GRID, shapesFor, type Vehicle } from '../../lib/vehicles'
import { filmScript, type FilmFit, type FilmLength, type FilmScript, type ScriptWish } from '../../lib/filmScript'
import {
  TILT, heightAtPlace, profilePosition, project, rowSource, tourTilesFor, visibleGround,
  type Profile, type TourCamera, type TourScript,
} from '../../lib/tourScript'
import { groesse } from '../../lib/format'
import { notiere } from '../../lib/sendeprotokoll'
import { datenrate, ersteMoegliche, familie, nachladen, ZIELKODIERUNGEN, type Kodiererwahl } from '../../lib/videowandeln'
import type { AnimationValues } from '../settings/AnimationFields'
import { WORTLAUT } from '../../texte'
import { drawPhotoPin, PHOTO_PIN_FILM } from './photoPin'
import type { TerrainGround, TerrainSpent, TerrainStart } from './terrainGround'
import { TERRAIN_CREDIT } from '../../lib/terrainSource'
import type { VehicleView, Vehicles3d } from './vehicle3d'
import { SPRITE_BASE, seatOnWay, stampSolid, type ModelExtent } from '../../lib/vehicle3dView'
import { drawCompass, northOnScreen } from '../../lib/filmCompass'

/**
 * The film of a day's route (#90, 1.96.0): the camera of `lib/tourScript.ts`
 * over the script of `lib/filmScript.ts`, drawn frame by frame and encoded as
 * MP4 -- **in the browser**, because the server has no ffmpeg and no process
 * that runs for long. The same tools as the video upload: WebCodecs and
 * `mp4-muxer`, loaded on first use.
 *
 * **One way since 1.125.0.** 1.118.0 (#139) built the complex film as a second
 * way beside this one, from use "2 komplett unabhängige Wege", with its own
 * loop written a second time on purpose. From use then: "Codebasis ‚einfach'
 * und ‚komplex' zusammenlegen". The choices (following, tilted, profile, map)
 * are four switches now; with all of them off the camera is the simple
 * script's frame for frame (`tourScript()`), and the picture is drawn flat.
 *
 * **The tilted picture is drawn with a plain 2D canvas**: the ground is drawn
 * flat and turned into a canvas of its own, and then copied into the frame
 * row by row, each row from the ground row and at the width `rowSource()`
 * gives. For a flat ground that is the exact perspective, needs no WebGL, and
 * the Prüfstand can measure it. The heights of the profile come from this
 * server's own files.
 *
 * **The tiles come from the map server, as on every map here** -- the first
 * exception in `CLAUDE.md`, not a new one. The film needs more of them than
 * the still: every view and every step of a flight. Loaded once before the
 * first frame, at most `MAX_TILES`; beyond that the film takes a coarser
 * level, a quarter of the tiles per level. The number stands in the
 * diagnosis.
 *
 * **Nothing here talks to our server.** The caller uploads the result after
 * the sheet has finished; cancelled, nothing has left the device (decided:
 * "bei Abbruch des Benutzers / Verlassen der App: nichts am Server").
 */

/**
 * The film's size and rate (1.107.0): from the settings of an uploaded
 * video, `video_max_edge` as the long side and `video_max_fps`. From use:
 * "bitte aus den Einstellungen nehmen". Until then the film was a fixed
 * 1280 x 800 at 30 frames, whatever the installation allowed for a video.
 * The shape is 16:9 (1.108.0), from use: "Seitenverhältnis 16:9 - wie ein
 * echtes Video". The 16:10 of 1.96.0 had no reason, and at 1920 it is over
 * H.264 Level 4 (1920 x 1200 are 9000 macroblocks, the level holds 8192),
 * where 1920 x 1080 just fits. The data rate follows the rule of an uploaded
 * video (`datenrate()`), so that both come out alike.
 */
export interface FilmFormat {
  width: number
  height: number
  fps: number
}

/**
 * The film until 1.106.0. Two things hang on it: the overlays (names, pins,
 * vehicle, attribution) are drawn in its units and scaled to the film, so
 * that a bigger film is sharper and not a smaller picture; and it is the
 * step back for a device that cannot encode the wished size.
 */
export const BASE_FORMAT: FilmFormat = { width: 1280, height: 720, fps: 30 }

/** The format from the settings; the base without them. H.264 wants even sides. */
export function filmFormat(values: Pick<Settings, 'video_max_edge' | 'video_max_fps'> | null): FilmFormat {
  if (values === null) return BASE_FORMAT
  const even = (n: number) => Math.max(2, Math.round(n / 2) * 2)
  const width = even(values.video_max_edge)
  return {
    width,
    height: even((width * BASE_FORMAT.height) / BASE_FORMAT.width),
    fps: Math.max(1, Math.round(values.video_max_fps)),
  }
}

/**
 * The wished format if this device can encode it, else the base, else
 * `null`. A telephone at Level 4 encodes no 3840 x 2160; a film in 1280 x 720
 * is better than the still.
 */
export async function encodableFormat(wished: FilmFormat, codecs?: readonly string[]): Promise<EncodableFormat | null> {
  for (const format of [wished, { ...BASE_FORMAT, fps: wished.fps }]) {
    const choice = await ersteMoegliche(codecs ?? ZIELKODIERUNGEN, format.width, format.height, format.fps)
    // The encoder found here goes to `buildRouteFilm()` (1.112.0, review):
    // it asked the same question a second time.
    if (choice !== null) return { format, choice }
  }
  return null
}

/** A format this device encodes, and the encoder that said so. */
export interface EncodableFormat {
  format: FilmFormat
  choice: Kodiererwahl
}

/**
 * The film settings out of the animation fields of the map style (1.112.0,
 * review) -- the tenths of the sliders become seconds here and nowhere
 * else. Stood three times (the sheet's preview, its answer, the plus menu).
 */
export function filmSettingsFrom(values: AnimationValues, bounds: FilmLength, fit: FilmFit | null = null): FilmSettings {
  return {
    // The longest film is the card's own choice within the server's bounds (1.122.0).
    length: { min: bounds.min, max: Math.min(bounds.max, Math.max(bounds.min, values.daycard_anim_max)) },
    ceiling: bounds.max,
    fit,
    mode: values.daycard_anim_mode,
    seconds: values.daycard_anim_seconds,
    rate: values.daycard_anim_rate / 10,
    pause: values.daycard_anim_pause / 10,
    // Only when there is one (1.150.0): the settings are part of a video's
    // fingerprint (`filmInput()`), and a film without a stop at a change
    // keeps the fingerprint it had before -- and with it its video.
    ...(values.daycard_anim_change > 0 ? { change: values.daycard_anim_change / 10 } : {}),
    pins: values.daycard_anim_pins === 'on',
  }
}

/**
 * The script's wish from the settings; without a tile source the zoom is the
 * film's own cap. `bottom` is what the picture covers at the bottom
 * (`profileCover()`, 1.148.0).
 */
export function wishFrom(settings: FilmSettings, source: MapConfig | null, bottom = 0): ScriptWish {
  return {
    bottom,
    mode: settings.mode,
    seconds: settings.seconds,
    rate: settings.rate,
    pause: settings.pause,
    change: settings.change,
    pinsWhenPassing: settings.pins,
    maxZoom: source === null ? FILM_MAX_ZOOM : Math.min(source.max_zoom, FILM_MAX_ZOOM),
    length: settings.length,
    ceiling: settings.ceiling,
    fit: settings.fit,
  }
}

/** The closest the film's camera goes -- closer, and a street is a grey field at 1280 points. */
export const FILM_MAX_ZOOM = 16
/** Tiles fetched at once: as many as a browser opens to one host. */
const CONCURRENT = 6
/** More tiles than this, and the film takes one level less. */
export const MAX_TILES = 500
const TILE = 256

export interface FilmSettings {
  /** The bounds of the length, from the server (`film_length`). */
  length: FilmLength
  mode: 'fixed' | 'distance'
  seconds: number
  /** `distance`: seconds per ten kilometres, 0.1 to 1. */
  rate: number
  /** Seconds the vehicle stands at each highlight it passes (1.100.0). */
  pause: number
  /** Seconds the film waits where the vehicle changes (1.150.0); missing is none. */
  change?: number
  pins: boolean
  /** The server's longest film (1.149.0): how far "länger" may go. */
  ceiling?: number
  /** The answer when the film is longer than its limit (1.149.0, `FilmTiming`). */
  fit?: FilmFit | null
}

/** The day card's stroke, the shape `zeichneTagesroute()` takes as well. */
export interface Stroke {
  farbe: string
  breite: number
  nadel: string
}

export interface Film {
  video: Blob
  width: number
  height: number
  poster: Blob
  durationMs: number
  /** The codec the film was written with, for the diagnosis. */
  codec: string
  /** Whether it stands on the relief (#140): asked for and fallen back is `false`, and the caller says so. */
  terrain: boolean
  /** Whether the vehicle is in 3D in every frame (1.137.0): asked for and fallen back is `false`, and the caller says so. */
  vehicle3d: boolean
}

export interface FilmSignal {
  cancelled: boolean
}

export type FilmStage = 'map' | 'compute'

/** Whether this browser can make a film at all. */
export function canAnimate(): boolean {
  return typeof VideoEncoder !== 'undefined' && typeof VideoFrame !== 'undefined' && typeof OffscreenCanvas !== 'undefined'
}

/** The script for a preview, or `null` when there is nothing to move. */
export function scriptFor(preview: DayCardPreview, source: MapConfig | null, settings: FilmSettings, format: FilmFormat, bottom = 0): FilmScript | null {
  return filmScript(preview.sections ?? [], preview.highlights ?? [], format, wishFrom(settings, source, bottom))
}

/**
 * The picture points the height profile covers at the bottom of a film in
 * `format`, with a little air above it (1.148.0). From use, with a film:
 * "die Endposition wird durch das Höhenprofil verdeckt". Built from the
 * constants `drawProfile()` draws with, so the two cannot drift apart. The
 * profile is not hidden at the end instead: there it shows the whole curve.
 */
export function profileCover(format: FilmFormat): number {
  return (PROFILE_HEIGHT + PROFILE_MARGIN + PROFILE_AIR) * (format.width / BASE_FORMAT.width)
}

export function throwIfCancelled(signal: FilmSignal | undefined): void {
  if (signal?.cancelled) throw new Error(WORTLAUT.technik.routeFilm.cancelled)
}

export function tileUrl(template: string, z: number, x: number, y: number): string {
  const n = 2 ** z
  const xw = ((x % n) + n) % n
  return template
    .replace('{s}', 'abc'[(xw + y) % 3]!)
    .replace('{z}', String(z))
    .replace('{x}', String(xw))
    .replace('{y}', String(y))
    .replace('{r}', '')
}

/** One tile as a picture; `null` when the server says nothing or refuses CORS. */
export function loadTile(url: string): Promise<HTMLImageElement | null> {
  return new Promise((resolve) => {
    const image = new Image()
    // Anonymous CORS, or the canvas is tainted and no frame can be read.
    image.crossOrigin = 'anonymous'
    // The tile server's rule, the same as `kacheloptionen()`: only the name
    // of this page goes along, never the address of the trip.
    image.referrerPolicy = 'strict-origin-when-cross-origin'
    const done = (ok: boolean) => {
      clearTimeout(timer)
      resolve(ok ? image : null)
    }
    // A tile server that stays silent costs a grey square, not the film.
    const timer = setTimeout(() => done(false), 12000)
    image.onload = () => done(true)
    image.onerror = () => done(false)
    image.src = url
  })
}

/** The attribution as plain text: the source gives HTML with `&copy;` and a link. */
export function attributionText(html: string): string {
  const doc = new DOMParser().parseFromString(`<p>${html}</p>`, 'text/html')
  return (doc.body.textContent ?? '').replace(/\s+/g, ' ').trim()
}

export async function loadTiles(
  keys: Set<string>,
  source: MapConfig,
  onShare: (share: number) => void,
  signal: FilmSignal | undefined,
): Promise<Map<string, HTMLImageElement | null>> {
  const list = [...keys]
  const images = new Map<string, HTMLImageElement | null>()
  let next = 0
  let finished = 0
  let loaded = 0
  const worker = async () => {
    while (next < list.length) {
      throwIfCancelled(signal)
      // A map server that has not given a single one of the first dozen
      // tiles will not give the rest: stop asking. Otherwise a hundred
      // tiles wait twelve seconds each, six at a time.
      if (loaded === 0 && finished >= CONCURRENT * 2) return
      const key = list[next++]!
      const [z, x, y] = key.split('/').map(Number) as [number, number, number]
      const image = await loadTile(tileUrl(source.tile_url, z, x, y))
      images.set(key, image)
      if (image !== null) loaded++
      onShare(++finished / list.length)
    }
  }
  await Promise.all(Array.from({ length: Math.min(CONCURRENT, list.length) }, worker))
  return images
}

/** A vehicle's shapes as paths for the canvas, built once per film. */
export type PaintedShape = { path: Path2D; fill: string; stroke?: string; width?: number }
export type PaintedArt = Record<Vehicle, { view: 'side' | 'top'; east: PaintedShape[]; west: PaintedShape[] }>

const PIN = new Path2D('M11 30C11 30 0 18.5 0 11a11 11 0 1 1 22 0c0 7.5-11 19-11 19z')

/** The pictures of the points, by address; `null` when one did not load. */
export type PinImages = Map<string, HTMLImageElement | null>

/**
 * The longest name next to a pin, in film pixels. Longer ones end in "…":
 * a label is a hint which point it is, the card under the post has the rest.
 */
const LABEL_WIDTH = 300

/** A point's name, short enough for its label. */
function labelText(pen: OffscreenCanvasRenderingContext2D, title: string): string {
  if (pen.measureText(title).width <= LABEL_WIDTH) return title
  let text = title
  while (text.length > 1 && pen.measureText(`${text}…`).width > LABEL_WIDTH) text = text.slice(0, -1)
  return `${text.trimEnd()}…`
}

/**
 * The pins of the points that stand by now (#90), since 1.99.0 with the
 * point's picture and its name. From use: "bei den Highlights sollte der
 * Text dabei stehen (und vielleicht auch das Bild wie auf der Karte)".
 *
 * A point with a picture gets the round photo pin of the map (`nadelbild`,
 * `nadel--foto`): the picture in a ring of the pin colour over a short tip.
 * Without one it keeps the drop. **The labels go newest first, and a label
 * that would cover one already drawn is left out**: at the end the whole
 * day stands with every pin, and twelve names on top of each other say
 * nothing. The pin itself always stands.
 */
export function drawPins(
  pen: OffscreenCanvasRenderingContext2D,
  script: FilmScript,
  shown: readonly number[],
  point: (x: number, y: number) => [number, number],
  colour: string,
  pinImages: PinImages,
): void {
  const heads: { i: number; x: number; y: number; reach: number }[] = []
  for (const i of shown) {
    const pin = script.pins[i]!
    const [px, py] = point(pin.x, pin.y)
    const photo = pin.image === null ? null : (pinImages.get(pin.image) ?? null)
    if (photo !== null) {
      const { cx, cy } = drawPhotoPin(pen, px, py, photo, colour, PHOTO_PIN_FILM)
      heads.push({ i, x: cx, y: cy, reach: 30 })
      continue
    }
    pen.save()
    pen.translate(px - 16.5, py - 45)
    pen.scale(1.5, 1.5)
    pen.fillStyle = colour
    pen.fill(PIN)
    pen.lineWidth = 1.5
    pen.strokeStyle = '#ffffff'
    pen.stroke(PIN)
    pen.beginPath()
    pen.arc(11, 11, 4, 0, Math.PI * 2)
    pen.fillStyle = '#ffffff'
    pen.fill()
    pen.restore()
    heads.push({ i, x: px, y: py - 28, reach: 20 })
  }

  pen.save()
  pen.font = '600 22px -apple-system, system-ui, sans-serif'
  pen.textBaseline = 'middle'
  const taken: [number, number, number, number][] = []
  for (const head of [...heads].reverse()) {
    const title = script.pins[head.i]!.title
    if (title === '') continue
    const text = labelText(pen, title)
    const w = pen.measureText(text).width + 20
    const h = 34
    let x = head.x + head.reach
    if (x + w > BASE_FORMAT.width - 8) x = head.x - head.reach - w
    const y = Math.min(BASE_FORMAT.height - h - 8, Math.max(8, head.y - h / 2))
    if (taken.some(([ax, ay, aw, ah]) => x < ax + aw && ax < x + w && y < ay + ah && ay < y + h)) continue
    taken.push([x, y, w, h])
    pen.shadowColor = 'rgba(0,0,0,0.3)'
    pen.shadowBlur = 6
    pen.fillStyle = 'rgba(255,255,255,0.94)'
    pen.beginPath()
    pen.roundRect(x, y, w, h, 9)
    pen.fill()
    pen.shadowColor = 'transparent'
    pen.fillStyle = '#1c1c1e'
    pen.fillText(text, x + 10, y + h / 2 + 1)
  }
  pen.restore()
}

/** The strips of the tilted picture, in picture pixels. */
const STRIP = 2
/** The ground canvas has at most this many times the pixels of the frame. */
const GROUND_AREA = 1.6
/** The profile at the bottom, in the units of the base film. */
const PROFILE_HEIGHT = 110
const PROFILE_MARGIN = 14
/** Air between the profile's strip and what the camera fits above it (1.148.0). */
const PROFILE_AIR = 8

interface TileSet {
  coarser: number
  images: Map<string, HTMLImageElement | null>
}

/** Every tile the film needs, at a level that stays under the limit; beyond it a coarser level. */
function tileList(tour: TourScript, maxZoom: number, format: FilmFormat): { coarser: number; keys: Set<string> } {
  const frames = Math.ceil(tour.duration * format.fps)
  for (let coarser = 0; ; coarser++) {
    const keys = new Set<string>()
    for (let i = 0; i <= frames; i++) {
      const k = tourTilesFor(tour.at(i / format.fps).camera, format, maxZoom, coarser)
      for (const [x, y] of k.tiles) keys.add(`${k.z}/${x}/${y}`)
    }
    if (keys.size <= MAX_TILES || coarser >= 4) return { coarser, keys }
  }
}

/** The vehicles' shapes as paths, once per film. */
function paintedArt(): PaintedArt {
  const paint = (shapes: typeof VEHICLE_ART.car.shapes): PaintedShape[] =>
    shapes.map((s) => ({ path: new Path2D(s.d), fill: s.fill, stroke: s.stroke, width: s.width }))
  return Object.fromEntries(Object.entries(VEHICLE_ART).map(([v, a]) => [v, {
    view: a.view,
    east: paint(shapesFor(a, false)),
    west: paint(shapesFor(a, true)),
  }])) as PaintedArt
}

/**
 * The ground: tiles, the way and the discs of start and end, flat and turned,
 * in ground pixels. `pen` carries the transform from ground pixels to its
 * canvas; here everything is placed in **unturned** pixels around the
 * camera's centre, computed in doubles, as the simple film does -- a canvas
 * transform at zoom 16 would lose the pixel in single precision.
 */
function drawGround(
  pen: OffscreenCanvasRenderingContext2D,
  toCanvas: [number, number, number, number, number, number],
  tour: TourScript,
  t: number,
  camera: TourCamera,
  format: FilmFormat,
  tiles: TileSet,
  maxZoom: number,
  stroke: Stroke,
  unit: number,
): void {
  const state = tour.at(t)
  const scale = TILE * 2 ** camera.zoom
  const c = Math.cos(camera.rotation)
  const s = Math.sin(camera.rotation)
  const [a, b, cc, d, e, f] = toCanvas
  // Unturned pixels → ground pixels (turn) → canvas.
  pen.setTransform(a * c + cc * s, b * c + d * s, -a * s + cc * c, -b * s + d * c, e, f)

  const k = tourTilesFor(camera, format, maxZoom, tiles.coarser)
  const n = 2 ** k.z
  const size = TILE * 2 ** (camera.zoom - k.z)
  for (const [x, y] of k.tiles) {
    const image = tiles.images.get(`${k.z}/${x}/${y}`)
    if (!image) continue
    pen.drawImage(image, (x / n - camera.x) * scale - 0.5, (y / n - camera.y) * scale - 0.5, size + 1, size + 1)
  }

  const px = (x: number, y: number): [number, number] => [(x - camera.x) * scale, (y - camera.y) * scale]
  const width = Math.max(3, stroke.breite * 2) * unit
  pen.lineCap = 'round'
  pen.lineJoin = 'round'
  pen.globalAlpha = 0.3
  pen.strokeStyle = stroke.farbe
  pen.lineWidth = width
  for (const line of tour.base.lines) {
    pen.beginPath()
    line.xy.forEach(([x, y], i) => {
      const [qx, qy] = px(x, y)
      if (i === 0) pen.moveTo(qx, qy)
      else pen.lineTo(qx, qy)
    })
    pen.stroke()
  }
  pen.globalAlpha = 1
  tour.base.lines.forEach((line, j) => {
    const share = state.drawn[j] ?? 0
    if (share <= 0) return
    const target = share * line.length
    pen.beginPath()
    const [sx, sy] = px(line.xy[0]![0], line.xy[0]![1])
    pen.moveTo(sx, sy)
    for (let i = 1; i < line.xy.length; i++) {
      const [ax, ay] = line.xy[i - 1]!
      const [bx, by] = line.xy[i]!
      if (line.sofar[i]! <= target) {
        const [qx, qy] = px(bx, by)
        pen.lineTo(qx, qy)
        continue
      }
      const from = line.sofar[i - 1]!
      const u = line.sofar[i]! > from ? (target - from) / (line.sofar[i]! - from) : 0
      const [qx, qy] = px(ax + (bx - ax) * u, ay + (by - ay) * u)
      pen.lineTo(qx, qy)
      break
    }
    pen.stroke()
  })

  const first = tour.base.lines[0]!.xy[0]!
  const lastLine = tour.base.lines[tour.base.lines.length - 1]!
  const last = lastLine.xy[lastLine.xy.length - 1]!
  for (const [x, y] of [first, last]) {
    const [qx, qy] = px(x, y)
    pen.beginPath()
    pen.arc(qx, qy, 12 * unit, 0, Math.PI * 2)
    pen.fillStyle = stroke.farbe
    pen.fill()
    pen.lineWidth = 4 * unit
    pen.strokeStyle = '#ffffff'
    pen.stroke()
  }
  pen.setTransform(1, 0, 0, 1, 0, 0)
}

/** The ground canvas: big enough for the full tilt, at most `GROUND_AREA` times the frame. */
function groundCanvas(format: FilmFormat): { canvas: OffscreenCanvas; pen: OffscreenCanvasRenderingContext2D; q: number } {
  const most = visibleGround({ x: 0.5, y: 0.5, zoom: 10, rotation: 0, tilt: TILT }, format)
  const w = most.x1 - most.x0
  const h = most.y1 - most.y0
  const q = Math.min(1, Math.sqrt((GROUND_AREA * format.width * format.height) / (w * h)))
  const canvas = new OffscreenCanvas(Math.ceil(w * q) + 2, Math.ceil(h * q) + 2)
  const pen = canvas.getContext('2d', { alpha: false })
  if (pen === null) throw new Error(WORTLAUT.technik.video.keineLeinwand)
  return { canvas, pen, q }
}

/** The profile of the heights at the bottom, with the vehicle's place on it (C). */
function drawProfile(
  pen: OffscreenCanvasRenderingContext2D,
  profile: Profile,
  position: number,
  stroke: Stroke,
  heightLabel: (metres: number) => string,
): number {
  const left = PROFILE_MARGIN
  const right = BASE_FORMAT.width - PROFILE_MARGIN
  const top = BASE_FORMAT.height - PROFILE_MARGIN - PROFILE_HEIGHT
  const bottom = BASE_FORMAT.height - PROFILE_MARGIN
  pen.save()
  pen.fillStyle = 'rgba(255,255,255,0.86)'
  pen.beginPath()
  pen.roundRect(left, top, right - left, bottom - top, 12)
  pen.fill()
  // A flat day is not drawn as a mountain: at least fifty metres of height.
  const range = Math.max(50, profile.high - profile.low)
  const x0 = left + 14
  const x1 = right - 110
  const y0 = top + 14
  const y1 = bottom - 12
  const xOf = (m: number) => x0 + ((x1 - x0) * m) / Math.max(1, profile.total)
  const yOf = (h: number) => y1 - ((y1 - y0) * (h - profile.low)) / range
  // The area under the heights up to `until` metres, cut exactly there:
  // between two entries the edge is placed where the vehicle is.
  const area = (until: number, fill: string) => {
    pen.fillStyle = fill
    pen.beginPath()
    let open = false
    let firstX = 0
    let lastX = 0
    const close = () => {
      if (!open) return
      pen.lineTo(lastX, y1)
      pen.lineTo(firstX, y1)
      pen.closePath()
      open = false
    }
    const to = (m: number, h: number) => {
      const x = xOf(m)
      if (!open) {
        pen.moveTo(x, y1)
        pen.lineTo(x, yOf(h))
        firstX = x
        open = true
      } else pen.lineTo(x, yOf(h))
      lastX = x
    }
    for (let i = 0; i < profile.at.length; i++) {
      const m = profile.at[i]!
      const h = profile.height[i] ?? null
      if (m > until) {
        const edge = heightAtPlace(profile, until)
        if (open && edge !== null) to(until, edge)
        break
      }
      if (h === null) close()
      else to(m, h)
    }
    close()
    pen.fill()
  }
  area(Infinity, 'rgba(60,60,67,0.18)')
  area(position, stroke.farbe)

  const here = heightAtPlace(profile, Math.min(profile.total, position))
  if (here !== null) {
    const x = xOf(Math.min(profile.total, position))
    const y = yOf(here)
    pen.beginPath()
    pen.arc(x, y, 7, 0, Math.PI * 2)
    pen.fillStyle = '#ffffff'
    pen.fill()
    pen.lineWidth = 3
    pen.strokeStyle = stroke.farbe
    pen.stroke()
    pen.font = '600 24px -apple-system, system-ui, sans-serif'
    pen.textBaseline = 'middle'
    pen.fillStyle = '#1c1c1e'
    pen.fillText(heightLabel(here), x1 + 16, (top + bottom) / 2)
  }
  pen.restore()
  return top
}

/** The 3D vehicle as `drawFrame()` asks for it (1.137.0, 1.143.0). */
interface Solid {
  picture(view: VehicleView): CanvasImageSource | null
  extent(vehicle: Vehicle): ModelExtent | null
}

/**
 * How far the road around the vehicle reaches on the picture, each way, in
 * units of the base film: the longest model (`HALF_VIEW` both ways) and the
 * rounding over it (`SEAT_ROUND`, a bell three widths wide) fit in it, and
 * more points only cost projections.
 */
const WAY_AROUND = SPRITE_BASE * 3

/**
 * The road around the vehicle on the picture (1.144.0): the section's points
 * before and after it, projected through the same `point()` as the vehicle,
 * until `WAY_AROUND` is passed each way, and the vehicle's own point among
 * them. Returns the way and how far along it that point lies. A point that
 * does not project (off the relief) ends the way on that side, and
 * `seatOnWay()` carries it on straight.
 */
function wayAround(
  line: { xy: [number, number][]; sofar: number[] } | undefined,
  along: number,
  here: [number, number],
  point: (x: number, y: number) => [number, number],
): { way: [number, number][]; at: number } {
  if (line === undefined) return { way: [here], at: 0 }
  const far = (q: [number, number]) => Math.hypot(q[0] - here[0], q[1] - here[1]) > WAY_AROUND
  let last = line.sofar.length - 1
  while (last > 0 && line.sofar[last]! >= along) last--
  const before: [number, number][] = []
  for (let i = last; i >= 0; i--) {
    const q = point(line.xy[i]![0], line.xy[i]![1])
    if (q[0] <= -9998) break
    before.push(q)
    if (far(q)) break
  }
  const after: [number, number][] = []
  for (let i = last + 1; i < line.xy.length; i++) {
    if (line.sofar[i]! <= along) continue
    const q = point(line.xy[i]![0], line.xy[i]![1])
    if (q[0] <= -9998) break
    after.push(q)
    if (far(q)) break
  }
  const way = [...before.reverse(), here, ...after]
  let at = 0
  for (let i = 1; i <= before.length; i++) at += Math.hypot(way[i]![0] - way[i - 1]![0], way[i]![1] - way[i - 1]![1])
  return { way, at }
}

/** One frame: the ground, flat or tilted, and what stands upright on it. */
function drawFrame(
  pen: OffscreenCanvasRenderingContext2D,
  ground: { canvas: OffscreenCanvas; pen: OffscreenCanvasRenderingContext2D; q: number },
  format: FilmFormat,
  tour: TourScript,
  t: number,
  tiles: TileSet,
  maxZoom: number,
  stroke: Stroke,
  attribution: string,
  art: PaintedArt,
  pinImages: PinImages,
  profile: Profile | null,
  heightLabel: (metres: number) => string,
  northLetter: string,
  terrain: TerrainGround | null,
  /** The vehicle in 3D (1.137.0): its picture for this frame, or `null` for the symbol, and its length (1.143.0). */
  solid: Solid | null = null,
): void {
  const state = tour.at(t)
  const camera = state.camera
  const unit = format.width / BASE_FORMAT.width
  pen.setTransform(1, 0, 0, 1, 0, 0)
  pen.fillStyle = '#e5e3df'
  pen.fillRect(0, 0, format.width, format.height)

  if (terrain !== null) {
    // The relief (#140): MapLibre has drawn this frame already
    // (`prepare()`), line and end dots included.
    pen.drawImage(terrain.canvas, 0, 0, format.width, format.height)
  } else if (camera.tilt < 0.005) {
    // Flat: the ground is the picture, turned around its middle.
    drawGround(pen, [1, 0, 0, 1, format.width / 2, format.height / 2], tour, t, camera, format, tiles, maxZoom, stroke, unit)
  } else {
    const g = visibleGround(camera, format)
    const q = ground.q
    ground.pen.setTransform(1, 0, 0, 1, 0, 0)
    ground.pen.fillStyle = '#e5e3df'
    ground.pen.fillRect(0, 0, ground.canvas.width, ground.canvas.height)
    drawGround(ground.pen, [q, 0, 0, q, -g.x0 * q, -g.y0 * q], tour, t, camera, format, tiles, maxZoom, stroke, unit)
    // Row by row: each strip of the picture from its ground row, at its width.
    for (let v = -format.height / 2; v < format.height / 2; v += STRIP) {
      const row = rowSource(v + STRIP / 2, camera, format)
      const next = rowSource(v + STRIP, camera, format).gy
      const prev = rowSource(v, camera, format).gy
      const half = (format.width / 2) * row.k
      const sx = (-half - g.x0) * q
      const sy = (prev - g.y0) * q
      const sh = Math.max(0.5, (next - prev) * q)
      pen.drawImage(ground.canvas, sx, sy, half * 2 * q, sh, 0, v + format.height / 2, format.width, STRIP + 0.5)
    }
    // The far edge in a light haze: the eye reads distance, and the softer
    // far tiles do not look like a fault.
    const share = camera.tilt / TILT
    const haze = pen.createLinearGradient(0, 0, 0, format.height * 0.35)
    haze.addColorStop(0, `rgba(214,228,240,${(0.75 * share).toFixed(3)})`)
    haze.addColorStop(1, 'rgba(214,228,240,0)')
    pen.fillStyle = haze
    pen.fillRect(0, 0, format.width, format.height * 0.35)
  }

  // Everything that stands upright, in the units of the base film.
  pen.save()
  pen.scale(unit, unit)
  // On the relief a point stands at its height, so the pins and the vehicle
  // ask the map; flat, the script's own projection.
  const point = (x: number, y: number): [number, number] => {
    if (terrain !== null) {
      const q = terrain.point(x, y)
      return q === null ? [-9999, -9999] : [q[0] / unit, q[1] / unit]
    }
    const p = project(x, y, camera, format)
    return p === null ? [-9999, -9999] : [p.x / unit, p.y / unit]
  }
  drawPins(pen, tour.base, state.pins, point, stroke.nadel, pinImages)

  if (state.vehicle !== null) {
    const v = state.vehicle
    const [px, py] = point(v.x, v.y)
    // Its way on the picture: a step along its heading, projected.
    const step = 1 / (TILE * 2 ** camera.zoom)
    const [qx, qy] = point(v.x + Math.cos(v.heading) * step * 20, v.y + Math.sin(v.heading) * step * 20)
    const onScreen = Math.atan2(qy - py, qx - px)
    // A side view looks the way of the script's calm east or west: the map
    // turns at most 45 degrees (1.120.0), so its side is the picture's side.
    const westward = v.westward
    // The vehicle in 3D (1.137.0), where the switch is on and the module
    // drew. Without a picture, the symbol as before. It drives the road around
    // its point, rounded (1.144.0, `seatOnWay()`).
    const extent = solid === null ? null : solid.extent(v.vehicle)
    const around = extent === null ? null : wayAround(tour.base.lines[v.leg], v.along, [px, py], point)
    const seat = extent === null || around === null ? null : seatOnWay(around.way, around.at, extent, camera.tilt, onScreen)
    const solidPicture = solid === null || seat === null ? null : solid.picture({ vehicle: v.vehicle, onScreen: seat.onScreen, tilt: camera.tilt, t: state.moving })
    if (solidPicture !== null && seat !== null) {
      stampSolid(pen, solidPicture, seat.x, seat.y)
    } else {
    pen.save()
    pen.shadowColor = 'rgba(0,0,0,0.35)'
    pen.shadowBlur = 8
    pen.beginPath()
    pen.arc(px, py, 28, 0, Math.PI * 2)
    pen.fillStyle = '#ffffff'
    pen.fill()
    pen.shadowColor = 'transparent'
    pen.lineWidth = 3
    pen.strokeStyle = stroke.farbe
    pen.stroke()
    pen.translate(px, py)
    const picture = art[v.vehicle]
    if (picture.view === 'top') pen.rotate(onScreen)
    else if (westward) pen.scale(-1, 1)
    const scale = 42 / VEHICLE_GRID
    pen.scale(scale, scale)
    pen.translate(-VEHICLE_GRID / 2, -VEHICLE_GRID / 2)
    pen.lineCap = 'round'
    pen.lineJoin = 'round'
    for (const shape of picture.view === 'side' && westward ? picture.west : picture.east) {
      if (shape.fill !== 'none') {
        pen.fillStyle = shape.fill
        pen.fill(shape.path)
      }
      if (shape.stroke !== undefined) {
        pen.strokeStyle = shape.stroke
        pen.lineWidth = shape.width ?? 1
        pen.stroke(shape.path)
      }
    }
    pen.restore()
    }
  }

  // The north arrow (1.140.0): north measured through the same `point()` as
  // the pins and the vehicle, so it agrees flat, tilted and on the relief.
  drawCompass(pen, BASE_FORMAT.width, northOnScreen(point, camera.x, camera.y, camera.zoom), northLetter)

  let attributionBottom = BASE_FORMAT.height
  if (profile !== null) attributionBottom = drawProfile(pen, profile, profilePosition(tour.base, state), stroke, heightLabel) - 4

  // The attribution: a duty, in every frame, above the profile when there is one.
  if (attribution !== '') {
    pen.font = '16px sans-serif'
    const w = pen.measureText(attribution).width + 12
    pen.fillStyle = 'rgba(255,255,255,0.8)'
    pen.fillRect(BASE_FORMAT.width - w, attributionBottom - 24, w, 24)
    pen.fillStyle = '#333333'
    pen.fillText(attribution, BASE_FORMAT.width - w + 6, attributionBottom - 7)
  }
  pen.restore()
}

/**
 * The state of the drawing (1.132.0): part of every video's fingerprint.
 * **Whoever changes what stands in a film's picture counts it up** -- the
 * line, the pins, the vehicle, the profile, the camera, the relief. Without
 * that, a card changed with the same settings keeps a video drawn the old
 * way; the sheet's "Video neu rechnen" is the way out, not the rule.
 *
 * 2 (1.137.0): the camper's cab as a Sprinter's, dark rims.
 * 3 (1.139.0): the 3D vehicle without the disc, a quarter larger.
 * 4 (1.140.0): the vehicle turns smoothly, the following camera calmer, a north arrow top right.
 * 5 (1.143.0): the 3D vehicle's nose and rear on the line, in bends too.
 * 6 (1.144.0): the 3D vehicle drives a rounded road, its middle on it.
 * 7 (1.145.0): the relief's place names in the local language, Latin below.
 * 8 (1.146.0): the vehicle's road rounded exactly, it no longer shakes from frame to frame.
 * 9 (1.148.0): the whole day and each section fitted above the height profile.
 * 10 (1.149.0): each stop at a highlight as long as chosen, on top of the driving time.
 * 11 (1.150.1): wheels and legs of the 3D vehicle stand still while it does.
 */
export const FILM_DRAWING = 11

/**
 * The fingerprint of everything a video is made from (1.132.0): SHA-256 in
 * hex over the day as the server read it (without its timings), the
 * settings, the stroke, the format and `FILM_DRAWING`. From use: "warum
 * wird ein Video neu generiert, wenn in den Angaben nichts geändert wurde."
 * The sheet alone is not enough to say so -- a new recording, a new pin or
 * another colour change the video without changing the sheet -- so the
 * day goes into it as well. `null` where this browser cannot hash
 * (`crypto.subtle` needs a secure page), and then the video is made anew.
 */
export async function filmInput(parts: { preview: object; settings: unknown; tour: unknown; display: string; stroke: unknown; format: FilmFormat }): Promise<string | null> {
  if (typeof crypto === 'undefined' || crypto.subtle === undefined) return null
  const { timings: _timings, ...day } = parts.preview as { timings?: unknown }
  const text = JSON.stringify([FILM_DRAWING, day, parts.settings, parts.tour, parts.display, parts.stroke, parts.format])
  const hash = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(text))
  return [...new Uint8Array(hash)].map((b) => b.toString(16).padStart(2, '0')).join('')
}

/** What the server spent on the day it read (`timings` of the preview, 1.132.0). */
export interface DayTimings {
  day_ms: number
  heights_ms: number
  pins_ms: number
}

/** „Tag 4749 ms (Server: Tag 300, Höhen 3900, Punkte 40)“ -- the diagnosis, German like every line there. */
export function dayLine(dayMs: number | undefined, server: DayTimings | null | undefined): string {
  const head = `Tag ${dayMs ?? '–'} ms`
  return server ? `${head} (Server: Tag ${server.day_ms}, Höhen ${server.heights_ms}, Punkte ${server.pins_ms})` : head
}

/**
 * „Karte 9.1 = Linie 0.2 + Zeichnen 6.4 + Warten 2.5 (830 sofort, 11
 * gewartet, 1.9 Zeichnungen je Bild), Malen 1.0, Kodierer 13.5 = Bild 9.0 + Warteschlange 4.5;
 * Viertel 12/11/11/12“ -- milliseconds per frame. Without relief the map is
 * 0 and has no parts. The encoder is split since 1.133.0: making the
 * `VideoFrame` (where a WebGL picture is read back) against waiting for
 * the encoder's queue; on the iPad it took 13.5 ms with relief against 6.3
 * without.
 */
export function frameSplit(
  count: number,
  spent: { map: number; paint: number; frame: number; queue: number },
  map: TerrainSpent | null,
  quarters: readonly number[],
): string {
  const per = (ms: number, frames = count) => (ms / Math.max(1, frames)).toFixed(1)
  const mapPart = map === null
    ? `Karte ${per(spent.map)}`
    : `Karte ${per(spent.map)} = Linie ${per(map.line)} + Zeichnen ${per(map.drawn)} + Warten ${per(map.wait)} + Grafik ${per(map.gpu)} (${map.instant} sofort, ${map.waited} gewartet, ${per(map.renders)} Zeichnungen je Bild)`
  // Frame i belongs to quarter floor(4i / count); so many fall into each.
  const sizes = [0, 1, 2, 3].map((q) => Math.ceil(((q + 1) * count) / 4) - Math.ceil((q * count) / 4))
  const quarterPart = quarters.map((ms, q) => Math.round(ms / Math.max(1, sizes[q]!))).join('/')
  return `${mapPart}, Malen ${per(spent.paint)}, Kodierer ${per(spent.frame + spent.queue)} = Bild ${per(spent.frame)} + Warteschlange ${per(spent.queue)}; Viertel ${quarterPart}`
}

/**
 * „Gelände gewartet: relief 40 Bilder 5200 ms, ohne 3 Bilder 120 ms;
 * Kacheln geladen: relief 120, openmaptiles 300; Höhen: 120 Anfragen, 30
 * unter 20 ms, im Schnitt 85 ms, längste 400 ms“ -- the measurement of
 * 1.133.2 (`TerrainSpent`), German like every line of the diagnosis.
 */
export function terrainSplit(spent: TerrainSpent): string {
  const why = Object.entries(spent.why).sort((a, b) => b[1].ms - a[1].ms)
    .map(([what, w]) => `${what} ${w.frames} Bilder ${Math.round(w.ms)} ms`).join(', ')
  const tiles = Object.entries(spent.tiles).sort((a, b) => b[1] - a[1]).map(([what, n]) => `${what} ${n}`).join(', ')
  const h = spent.heights
  const heights = `${h.requests} Anfragen, ${h.quick} unter 20 ms, im Schnitt ${Math.round(h.ms / Math.max(1, h.requests))} ms, längste ${Math.round(h.longest)} ms`
  return `Gelände gewartet: ${why || 'nie'}; Kacheln geladen: ${tiles || 'keine'}; Höhen: ${heights}`
}

/**
 * „Vorrat: Probelauf 240 Bilder in 1600 ms, 155 Kacheln; beim Bau fertig
 * 140, unterwegs 12, neu 3“ (1.134.0): how far the store ran ahead of the
 * film. „neu“ are tiles the trial run did not ask for.
 */
export function storeSplit(spent: TerrainSpent): string {
  const s = spent.store
  return `Vorrat: Probelauf ${s.trial} Bilder in ${Math.round(s.trialMs)} ms, ${s.tiles} Kacheln; beim Bau fertig ${s.found.ready}, unterwegs ${s.found.running}, neu ${s.found.fresh}`
}

/**
 * The film for a camera script. Throws on cancel and when the device cannot
 * do it; the caller then sends nothing.
 *
 * `codecs` is a door for the Prüfstand, as `kodierungen` at
 * `verkleinereVideo()`: the Chromium there has no H.264.
 */
export async function buildRouteFilm(
  tour: TourScript,
  profile: Profile | null,
  source: MapConfig,
  stroke: Stroke,
  options: {
    onStage: (stage: FilmStage) => void
    onShare: (share: number) => void
    signal?: FilmSignal
    codecs?: readonly string[]
    choice?: Kodiererwahl
    /** The format the script was made for (`encodableFormat()`). */
    format: FilmFormat
    /** The height on the profile, „1.234 m“ -- the words come from the caller's texts. */
    heightLabel: (metres: number) => string
    /** The letter on the north arrow (1.140.0), from the caller's texts like the height. */
    northLetter: string
    /**
     * The fifth switch (#140): the ground with its relief from MapLibre
     * instead of the raster tiles, begun at the tap (`beginTerrain()`,
     * 1.130.0). When it cannot open (no WebGL, the style or the heights do
     * not come) the film goes without it -- nothing about it is fatal -- and
     * `Film.terrain` says so. The caller closes it as well; closing twice is
     * fine.
     */
    terrain?: Promise<TerrainStart> | null
    /**
     * The sixth switch (1.137.0): the vehicle as a 3D model
     * (`vehicle3d.ts`). Without WebGL, or when a frame fails, the symbol
     * stands in -- `Film.vehicle3d` says so.
     */
    vehicle3d?: boolean
    /** Milliseconds from the tap until the server had read the day, for the diagnosis line. */
    dayMs?: number
    /** What the server says it spent on the day (1.132.0), for the same line. */
    server?: DayTimings | null
  },
): Promise<Film> {
  const { onStage, onShare, signal, format, heightLabel, northLetter } = options
  if (!canAnimate()) throw new Error(WORTLAUT.technik.routeFilm.noEncoder)
  const maxZoom = Math.min(source.max_zoom, FILM_MAX_ZOOM)
  const began = performance.now()

  // The encoder first: a device that cannot write this film falls back to
  // the still (the caller), and it should not load the map for nothing.
  const choice = options.choice ?? await ersteMoegliche(options.codecs ?? ZIELKODIERUNGEN, format.width, format.height, format.fps)
  if (choice === null) throw new Error(WORTLAUT.technik.routeFilm.noEncoder)
  const targetFamily = familie(choice.codec)
  if (targetFamily === 'unbekannt') throw new Error(`Unbekannte Zielkodierung: ${choice.codec}`)

  // Always, with or without relief (1.132.0): from use, 4.7 s of the wait
  // before the bar were the day, and the server's share of it is the next
  // question.
  notiere(`Routenfilm: ${dayLine(options.dayMs, options.server)}`)
  onStage('map')
  // The muxer loads beside the map, not after it (1.130.0).
  const muxerModule = nachladen(() => import('mp4-muxer'))
  muxerModule.catch(() => {})
  const unit = format.width / BASE_FORMAT.width
  let terrain: TerrainGround | null = null
  if (options.terrain) {
    try {
      const start = await options.terrain
      const { openTerrainGround } = await nachladen(() => import('./terrainGround'))
      terrain = await openTerrainGround(start, format, tour, stroke, unit)
      // Milliseconds after the tap, each step on its own: they overlap,
      // and which of them the wait is made of is the question (#140).
      const t = terrain.times
      notiere(`Routenfilm: Gelände bereit nach ${t.firstPicture} ms (Tag ${options.dayMs ?? '–'} ms, Modul ${t.module} ms, Stil ${t.style} ms, erstes Bild ${t.firstPicture} ms)`)
    } catch (error) {
      notiere(`Routenfilm: Gelände nicht verfügbar (${error instanceof Error ? error.message : String(error)}), Film ohne Gelände`)
      terrain = null
    }
    throwIfCancelled(signal)
  }
  // The vehicle in 3D (1.137.0): the module only with the switch. Whatever
  // fails here or in a frame costs the 3D, never the film -- the symbol
  // stands in for it, and the diagnosis says why.
  let models: Vehicles3d | null = null
  const solidSpent = { frames: 0, ms: 0 }
  if (options.vehicle3d === true) {
    const from = performance.now()
    try {
      const { openVehicles3d } = await nachladen(() => import('./vehicle3d'))
      models = openVehicles3d(Math.round(SPRITE_BASE * unit))
      notiere(`Routenfilm: 3D-Fahrzeug bereit nach ${Math.round(performance.now() - from)} ms`)
    } catch (error) {
      notiere(`Routenfilm: 3D-Fahrzeug nicht verfügbar (${error instanceof Error ? error.message : String(error)}), Film mit Symbol`)
      models = null
    }
    throwIfCancelled(signal)
  }
  const solidFails = (error: unknown): null => {
    notiere(`Routenfilm: 3D-Fahrzeug fällt aus (${error instanceof Error ? error.message : String(error)}), weiter mit Symbol`)
    models?.close()
    models = null
    return null
  }
  const solid: Solid | null = models === null ? null : {
    picture(view) {
      if (models === null) return null
      const from = performance.now()
      try {
        const picture = models.draw(view)
        solidSpent.frames++
        solidSpent.ms += performance.now() - from
        return picture
      } catch (error) {
        return solidFails(error)
      }
    },
    extent(vehicle) {
      if (models === null) return null
      try {
        return models.extent(vehicle)
      } catch (error) {
        return solidFails(error)
      }
    },
  }
  // The raster tiles only for the film without relief: with it, the map
  // fetches its own.
  const { coarser, keys } = terrain === null ? tileList(tour, maxZoom, format) : { coarser: 0, keys: new Set<string>() }
  const images = await loadTiles(keys, source, onShare, signal)
  const missing = keys.size - [...images.values()].filter((i) => i !== null).length
  const heights = profile === null ? 'keine' : `${profile.low}–${profile.high} m`
  if (terrain !== null) notiere(`Routenfilm: Gelände ohne Rasterkacheln, Höhen ${heights}, ${Math.round(performance.now() - began)} ms`)
  else notiere(`Routenfilm: ${keys.size} Kacheln${coarser > 0 ? `, ${coarser} Stufe(n) grober` : ''}, ${missing} fehlen, Höhen ${heights}, ${Math.round(performance.now() - began)} ms`)
  if (keys.size > 0 && missing === keys.size) {
    terrain?.close()
    models?.close()
    throw new Error(WORTLAUT.technik.routeFilm.noMap)
  }
  // All at once (1.130.0): one after the other they added up before the bar.
  const pinUrls = [...new Set(tour.base.pins.map((p) => p.image).filter((u): u is string => u !== null))]
  const pinImages: PinImages = new Map(await Promise.all(pinUrls.map(async (url) => [url, await loadTile(url)] as const)))
  throwIfCancelled(signal)

  onStage('compute')
  onShare(0)
  const { Muxer, ArrayBufferTarget } = await muxerModule
  const muxer = new Muxer({
    target: new ArrayBufferTarget(),
    fastStart: 'in-memory',
    video: { codec: targetFamily, width: format.width, height: format.height },
  })
  let failure: Error | null = null
  const encoder = new VideoEncoder({
    output: (chunk, meta) => muxer.addVideoChunk(chunk, meta),
    error: (error) => {
      failure = error instanceof Error ? error : new Error(String(error))
    },
  })
  const close = () => {
    try {
      if (encoder.state !== 'closed') encoder.close()
    } catch {
      // Already closed.
    }
    terrain?.close()
    models?.close()
    models = null
  }

  try {
    encoder.configure({
      codec: choice.codec,
      width: format.width,
      height: format.height,
      hardwareAcceleration: choice.hardware,
      bitrate: datenrate(format.width, format.height, format.fps),
      framerate: format.fps,
    })
    const canvas = new OffscreenCanvas(format.width, format.height)
    const pen = canvas.getContext('2d', { alpha: false })
    if (pen === null) throw new Error(WORTLAUT.technik.video.keineLeinwand)
    const ground = groundCanvas(format)
    const tiles: TileSet = { coarser, images }
    const attribution = terrain !== null ? TERRAIN_CREDIT : attributionText(source.attribution)
    const art = paintedArt()

    const count = Math.ceil(tour.duration * format.fps)
    const frameDuration = 1e6 / format.fps
    let waitedOut = 0
    const door = (globalThis as { __tbTerrainCheck?: unknown }).__tbTerrainCheck
    const check = Array.isArray(door) ? (door as unknown[]) : null
    // The trial run (1.134.0): every fourth frame, so that the tiles of the
    // whole film are on their way before the first frame waits for one.
    if (terrain !== null) {
      const states: ReturnType<typeof tour.at>[] = []
      for (let i = 0; i < count; i += 4) states.push(tour.at(i / format.fps))
      terrain.preload(states)
    }
    const computeFrom = performance.now()
    // Where a frame's time goes (1.132.0): the map, the painting, the
    // encoder, and each quarter of the film on its own -- from use, the
    // longest film took 64 ms a frame against 42 for a shorter one.
    const spent = { map: 0, paint: 0, frame: 0, queue: 0 }
    const quarters = [0, 0, 0, 0]
    for (let i = 0; i < count; i++) {
      throwIfCancelled(signal)
      if (failure !== null) throw failure
      const frameFrom = performance.now()
      if (terrain !== null && await terrain.prepare(tour.at(i / format.fps))) waitedOut++
      const painted = performance.now()
      spent.map += painted - frameFrom
      if (terrain !== null && check !== null) {
        // The door of `gelaende.mjs`: where the film's own camera and the
        // map put the vehicle and both ends of the day, frame by frame. The
        // ends matter: the vehicle stands near the middle when followed,
        // and there the angle of view hardly shows.
        const state = tour.at(i / format.fps)
        const lines = tour.base.lines
        const lastLine = lines[lines.length - 1]!
        const points: [string, number, number][] = [
          ['start', lines[0]!.xy[0]![0], lines[0]!.xy[0]![1]],
          ['end', lastLine.xy[lastLine.xy.length - 1]![0], lastLine.xy[lastLine.xy.length - 1]![1]],
        ]
        if (state.vehicle !== null) points.push(['vehicle', state.vehicle.x, state.vehicle.y])
        for (const [what, x, y] of points) {
          const film = project(x, y, state.camera, format)
          check.push({ i, what, tilt: state.camera.tilt, rotation: state.camera.rotation, zoom: state.camera.zoom, x: state.camera.x, film: film === null ? null : [film.x, film.y], map: terrain.point(x, y) })
        }
        // Where the driven line ends on the map (1.133.0): a pixel of the
        // map's own picture a little behind the vehicle and one a little
        // ahead, on the line. The strong part must end at the vehicle, not
        // at a stored point and not at the end of the section.
        if (i % 5 === 0) {
          lines.forEach((line, k) => {
            const share = state.drawn[k] ?? 0
            if (share < 0.15 || share > 0.85) return
            for (const [what, metres] of [['behind', (share - 0.08) * line.length], ['ahead', (share + 0.08) * line.length]] as const) {
              const n = Math.max(1, line.sofar.findIndex((s) => s >= metres))
              const from = line.sofar[n - 1]!
              const u = line.sofar[n]! > from ? (metres - from) / (line.sofar[n]! - from) : 0
              const [ax, ay] = line.xy[n - 1]!
              const [bx, by] = line.xy[n]!
              const at = terrain.point(ax + (bx - ax) * u, ay + (by - ay) * u)
              if (at === null) continue
              const probe = new OffscreenCanvas(3, 3).getContext('2d')!
              probe.drawImage(terrain.canvas, Math.round(at[0]) - 1, Math.round(at[1]) - 1, 3, 3, 0, 0, 3, 3)
              check.push({ i, what, at, color: stroke.farbe, pixels: [...probe.getImageData(0, 0, 3, 3).data] })
            }
          })
        }
      }
      const drawFrom = performance.now()
      drawFrame(pen, ground, format, tour, i / format.fps, tiles, maxZoom, stroke, attribution, art, pinImages, profile, heightLabel, northLetter, terrain, solid)
      const encodeFrom = performance.now()
      spent.paint += encodeFrom - drawFrom
      const frame = new VideoFrame(canvas, { timestamp: Math.round(i * frameDuration), duration: Math.round(frameDuration) })
      encoder.encode(frame, { keyFrame: i % (format.fps * 2) === 0 })
      frame.close()
      const queueFrom = performance.now()
      spent.frame += queueFrom - encodeFrom
      while (encoder.encodeQueueSize > 6) await new Promise((r) => setTimeout(r, 4))
      const frameEnd = performance.now()
      spent.queue += frameEnd - queueFrom
      const quarter = Math.min(3, Math.floor((i * 4) / count))
      quarters[quarter] = (quarters[quarter] ?? 0) + frameEnd - frameFrom
      if (i % 10 === 0) {
        onShare(i / count)
        await new Promise((r) => setTimeout(r, 0))
      }
    }
    await encoder.flush()
    if (failure !== null) throw failure
    muxer.finalize()
    const video = new Blob([muxer.target.buffer], { type: 'video/mp4' })

    // The still: the last frame -- the whole day, flat, every pin, the full profile.
    if (terrain !== null) await terrain.prepare(tour.at(tour.duration))
    drawFrame(pen, ground, format, tour, tour.duration, tiles, maxZoom, stroke, attribution, art, pinImages, profile, heightLabel, northLetter, terrain, solid)
    const poster = await canvas.convertToBlob({ type: 'image/jpeg', quality: 0.85 })
    onShare(1)

    const ms = performance.now() - computeFrom
    notiere(`Routenfilm: ${format.width}x${format.height}, ${format.fps}/s, ${count} Bilder, ${(ms / Math.max(1, count)).toFixed(1)} ms je Bild (${frameSplit(count, spent, terrain?.spent ?? null, quarters)}), ${choice.codec} ${choice.hardware === 'prefer-hardware' ? 'Hardware' : 'Software'}, ${groesse(video.size)}${terrain !== null ? `, Gelände, ${waitedOut} Bilder nach 5 s ohne fertige Karte` : ''}`)
    if (terrain !== null) notiere(`Routenfilm: ${terrainSplit(terrain.spent)}`)
    if (terrain !== null) notiere(`Routenfilm: ${storeSplit(terrain.spent)}`)
    if (solid !== null) notiere(`Routenfilm: 3D-Fahrzeug in ${solidSpent.frames} Bildern, ${(solidSpent.ms / Math.max(1, solidSpent.frames)).toFixed(1)} ms je Bild${models === null ? ', ausgefallen' : ''}`)
    // A frame that failed closed the models: a film that fell back on the way is a film with the symbol.
    const solidAll = options.vehicle3d === true && models !== null
    return { video, poster, width: format.width, height: format.height, durationMs: Math.round(tour.duration * 1000), codec: choice.codec, terrain: terrain !== null, vehicle3d: solidAll }
  } finally {
    close()
  }
}
